@if (configuration?.composition) {
  <div
    class="bathroom-configuration"
    [class.embedded]="embedded"
    [class.desktop]="layoutMode === 'desktop'"
    [class.panel-only]="hideRender">
    <!-- Header fixe -->
    @if (!embedded) {
      <header class="config-header">
        <button class="back-button" (click)="goBack()">
          <fa-icon [icon]="faChevronLeft"></fa-icon>
          <span [innerHTML]="'configuration.mobile.bathroom.back' | translate"></span>
        </button>

        <div class="total-price">
          <span class="price-label" [innerHTML]="'configuration.mobile.bathroom.total_label' | translate"></span>
          <span class="price-value">{{ formatPrice(configuration.totalPrice) }}</span>
        </div>

        <button
          class="list-button"
          (click)="openSummary()"
          [disabled]="!configuration.isValid"
          [title]="'configuration.mobile.bathroom.view_summary' | translate">
          <fa-icon [icon]="listFa"></fa-icon>
        </button>
      </header>
    }

    <!-- Zone de rendu fixe (50% hauteur écran) -->
    @if (!hideRender) {
      <div class="render-zone" #renderZone>
        <div class="render-container">
          <ng-casto-bathroom-render
            [composition]="configuration.composition"
            [selectedElement]="uiSelection?.selectedElement"
            [isLoading]="configuration.isPreviewLoading"
            (elementClick)="onElementClick($event)">
          </ng-casto-bathroom-render>
        </div>
      </div>
    } @else {
      <div class="render-capture-zone" #renderZone>
        <ng-casto-bathroom-render
          [composition]="configuration.composition"
          [selectedElement]="null"
          [isLoading]="configuration.isPreviewLoading">
        </ng-casto-bathroom-render>
      </div>
    }

    <!-- Zone scrollable des accordéons -->
    <div class="accordion-zone" #accordionZone>
      <!-- Accordéon Dimensions -->
      <div class="accordion-item" [class.expanded]="uiSelection?.activeSection === 'dimensions'">
        <button class="accordion-header" (click)="setActiveSection('dimensions')">
          <span
            class="accordion-title"
            [innerHTML]="'configuration.mobile.bathroom.dimensions_title' | translate"></span>
          <i
            class="fas"
            [class.fa-chevron-down]="uiSelection?.activeSection !== 'dimensions'"
            [class.fa-chevron-up]="uiSelection?.activeSection === 'dimensions'"></i>
        </button>

        @if (uiSelection?.activeSection === 'dimensions') {
          <div class="accordion-content">
            <!-- Mode global : Dimension unique pour tout le meuble -->
            @if (configuration.availableOptions.dimensionDisplayType === 'global') {
              <div class="form-section">
                <label
                  class="form-label"
                  [innerHTML]="'configuration.mobile.bathroom.furniture_dimension' | translate"></label>
                <div class="button-group">
                  @for (widthOpt of configuration.availableOptions.globalWidths; track widthOpt.value) {
                    <button
                      class="option-button"
                      [class.active]="configuration.selectedGlobalWidth === widthOpt.value"
                      [class.disabled]="!widthOpt.available"
                      [disabled]="!widthOpt.available"
                      (click)="widthOpt.available && onGlobalWidthChange(widthOpt.value)">
                      {{ widthOpt.value }}
                    </button>
                  }
                </div>
                @if (isWidthExceedingFilter(configuration.selectedGlobalWidth)) {
                  <span class="width-warning">
                    <i class="fas fa-exclamation-triangle"></i>
                    {{
                      'configuration.mobile.bathroom.width_exceeds_filter'
                        | translate: { selectedWidth: configuration.selectedGlobalWidth, maxWidth: maxFurnitureWidth }
                    }}
                  </span>
                }
              </div>
            }

            <!-- Mode par rangée : Dimensions séparées pour tiroir haut et bas -->
            @if (configuration.availableOptions.dimensionDisplayType === 'perRow') {
              <!-- Dimension tiroir haut (rowIndex === 0, utilise topRowWidths) -->
              @if (getRowConfigByIndex(0); as topRowConfig) {
                <div class="form-section">
                  <label
                    class="form-label"
                    [innerHTML]="'configuration.mobile.bathroom.top_drawer_dimension' | translate"></label>
                  <div class="button-group">
                    @for (widthOpt of configuration.availableOptions.topRowWidths; track widthOpt.value) {
                      <button
                        class="option-button"
                        [class.active]="topRowConfig.selectedWidth === widthOpt.value"
                        [class.disabled]="!widthOpt.available"
                        [disabled]="!widthOpt.available"
                        (click)="widthOpt.available && onRowWidthChange(0, widthOpt.value)">
                        {{ widthOpt.value }}
                      </button>
                    }
                  </div>
                  @if (isWidthExceedingFilter(topRowConfig.selectedWidth)) {
                    <span class="width-warning">
                      <i class="fas fa-exclamation-triangle"></i>
                      {{
                        'configuration.mobile.bathroom.width_exceeds_filter'
                          | translate: { selectedWidth: topRowConfig.selectedWidth, maxWidth: maxFurnitureWidth }
                      }}
                    </span>
                  }
                </div>
              }

              <!-- Dimension tiroir bas (rowIndex === 1, utilise bottomRowWidths) -->
              @if (getRowConfigByIndex(1); as bottomRowConfig) {
                <div class="form-section">
                  <label
                    class="form-label"
                    [innerHTML]="'configuration.mobile.bathroom.bottom_drawer_dimension' | translate"></label>
                  <div class="button-group">
                    @for (widthOpt of configuration.availableOptions.bottomRowWidths; track widthOpt.value) {
                      <button
                        class="option-button"
                        [class.active]="bottomRowConfig.selectedWidth === widthOpt.value"
                        [class.disabled]="!widthOpt.available"
                        [disabled]="!widthOpt.available"
                        [title]="widthOpt.reason || ''"
                        (click)="widthOpt.available && onRowWidthChange(1, widthOpt.value)">
                        {{ widthOpt.value }}
                      </button>
                    }
                  </div>
                  @if (isWidthExceedingFilter(bottomRowConfig.selectedWidth)) {
                    <span class="width-warning">
                      <i class="fas fa-exclamation-triangle"></i>
                      {{
                        'configuration.mobile.bathroom.width_exceeds_filter'
                          | translate: { selectedWidth: bottomRowConfig.selectedWidth, maxWidth: maxFurnitureWidth }
                      }}
                    </span>
                  }
                </div>
              }
            }

            <!-- Offset (uniquement si allowOffset est true ET hasOffsetOption est true) -->
            @if (configuration.availableOptions.allowOffset) {
              @for (rowConfig of configuration.rowConfigurations; track rowConfig.rowIndex) {
                @if (rowConfig.rowIndex > 0 && rowConfig.hasOffsetOption) {
                  <div class="form-section">
                    <label
                      class="form-label"
                      [innerHTML]="
                        'configuration.mobile.bathroom.offset_label'
                          | translate: { min: rowConfig.minOffset, max: rowConfig.maxOffset }
                      ">
                    </label>
                    <div class="button-group offset-control">
                      <button
                        class="option-button offset-button"
                        (click)="decrementOffset(rowConfig.rowIndex, rowConfig.offset)"
                        [disabled]="rowConfig.offset <= rowConfig.minOffset">
                        <fa-icon [icon]="faMinus"></fa-icon>
                      </button>
                      <input
                        class="option-button active offset-value"
                        type="text"
                        inputmode="numeric"
                        pattern="[0-9]*"
                        [value]="rowConfig.offset"
                        [min]="rowConfig.minOffset"
                        [max]="rowConfig.maxOffset"
                        (change)="onRowOffsetInputChange(rowConfig.rowIndex, $event)" />
                      <button
                        class="option-button offset-button"
                        (click)="incrementOffset(rowConfig.rowIndex, rowConfig.offset)"
                        [disabled]="rowConfig.offset >= rowConfig.maxOffset">
                        <fa-icon [icon]="faPlus"></fa-icon>
                      </button>
                    </div>
                  </div>
                }
              }
            }
          </div>
        }
      </div>

      <!-- Accordéon Vasque -->
      @if (getAvailableBasinStylesCount() > 1 || configuration.composition.compositionConfiguration.allowDoubleBasin) {
        <div class="accordion-item" id="basin-accordion-item" [class.expanded]="uiSelection?.activeSection === 'basin'">
          <button class="accordion-header" (click)="setActiveSection('basin')">
            <span class="accordion-title" [innerHTML]="'configuration.mobile.bathroom.basin_title' | translate"></span>
            <i
              class="fas"
              [class.fa-chevron-down]="uiSelection?.activeSection !== 'basin'"
              [class.fa-chevron-up]="uiSelection?.activeSection === 'basin'"></i>
          </button>

          @if (uiSelection?.activeSection === 'basin') {
            <div class="accordion-content">
              <!-- Style de vasque (masqué si un seul style disponible) -->
              @if (getAvailableBasinStylesCount() > 1) {
                <div class="form-section">
                  <label
                    class="form-label"
                    [innerHTML]="'configuration.mobile.bathroom.basin_style_label' | translate"></label>
                  <div class="button-group">
                    @for (option of configuration.availableOptions.basinStyles; track option.value) {
                      <button
                        class="option-button"
                        [class.active]="configuration.selectedBasinStyle === option.value"
                        [class.disabled]="!option.available"
                        [disabled]="!option.available"
                        [title]="option.reason || ''"
                        (click)="option.available ? onBasinStyleChange(option.value) : null">
                        {{ option.value }}
                      </button>
                    }
                  </div>
                </div>
              }

              <!-- Type de vasque -->
              @if (configuration.composition.compositionConfiguration.allowDoubleBasin) {
                <div class="form-section">
                  <label
                    class="form-label"
                    [innerHTML]="'configuration.mobile.bathroom.basin_type_label' | translate"></label>
                  <div class="button-group">
                    @for (option of configuration.availableOptions.basinTypes; track option.value) {
                      <button
                        class="option-button"
                        [class.active]="configuration.selectedBasinType === option.value"
                        [class.disabled]="!option.available"
                        [disabled]="!option.available"
                        [title]="option.reason || ''"
                        (click)="option.available && onBasinTypeChange(option.value)">
                        {{ option.label }}
                      </button>
                    }
                  </div>
                </div>
              }
            </div>
          }
        </div>
      }

      <!-- Accordéon Finitions -->
      <div class="accordion-item" [class.expanded]="uiSelection?.activeSection === 'finitions'">
        <button class="accordion-header" (click)="setActiveSection('finitions')">
          <span class="accordion-title" [innerHTML]="'configuration.mobile.bathroom.finishes_title' | translate"></span>
          <i
            class="fas"
            [class.fa-chevron-down]="uiSelection?.activeSection !== 'finitions'"
            [class.fa-chevron-up]="uiSelection?.activeSection === 'finitions'"></i>
        </button>

        @if (uiSelection?.activeSection === 'finitions') {
          <div class="accordion-content">
            <p
              class="form-description"
              [innerHTML]="'configuration.mobile.bathroom.finishes_description' | translate"></p>

            <!-- Plans de toilette (normal puis intermédiaire) -->
            @for (rowConfig of getSortedRowConfigurations(); track rowConfig.rowIndex) {
              @if (rowConfig.availableCoverFinitions?.length > 0) {
                <div
                  class="form-section"
                  [id]="getFinitionSectionId({ type: 'cover', rowIndex: rowConfig.rowIndex })"
                  [class.highlighted]="isElementSelected({ type: 'cover', rowIndex: rowConfig.rowIndex })">
                  <label class="form-label">
                    @if (rowConfig.rowIndex === 0) {
                      <span [innerHTML]="'configuration.mobile.bathroom.toilet_top' | translate"></span>
                    } @else {
                      <span [innerHTML]="'configuration.mobile.bathroom.intermediate_toilet_top' | translate"></span>
                    }
                  </label>
                  <div class="finition-grid">
                    @for (finition of rowConfig.availableCoverFinitions; track finition.id) {
                      <button
                        class="finition-button"
                        [class.active]="rowConfig.coverFinitionId === finition.id"
                        (click)="onCoverFinitionChange(rowConfig.rowIndex, finition.id)">
                        @if (finition.imageUrl) {
                          <img [src]="finition.imageUrl" [alt]="finition.name" class="finition-image" />
                        }
                        <span class="finition-name">{{ finition.name }}</span>
                      </button>
                    }
                  </div>
                  @if (rowConfig.coverFinitionId) {
                    <button
                      class="apply-all-button"
                      [disabled]="!canApplyCoverFinitionToAll(rowConfig.coverFinitionId)"
                      (click)="onApplyCoverFinitionToAll(rowConfig.coverFinitionId!)">
                      <span [innerHTML]="'configuration.mobile.bathroom.apply_to_all' | translate"></span>
                    </button>
                  }
                </div>
              }
            }

            <!-- Tiroirs / Niches par rangée (rangée 1 puis rangée 2) -->
            @for (rowConfig of getSortedRowConfigurations(); track rowConfig.rowIndex) {
              @for (colConfig of rowConfig.columnConfigurations; track colConfig.columnIndex) {
                @if (colConfig.availableFinitions.length > 0) {
                  <div
                    class="form-section"
                    [id]="
                      getFinitionSectionId({
                        type: 'column',
                        rowIndex: rowConfig.rowIndex,
                        columnIndex: colConfig.columnIndex,
                      })
                    "
                    [class.highlighted]="
                      isElementSelected({
                        type: 'column',
                        rowIndex: rowConfig.rowIndex,
                        columnIndex: colConfig.columnIndex,
                      })
                    ">
                    <label
                      class="form-label"
                      [innerHTML]="
                        (isNiche(colConfig)
                          ? 'configuration.mobile.bathroom.niche_label'
                          : 'configuration.mobile.bathroom.drawer_label'
                        )
                          | translate
                            : { row: rowConfig.rowIndex + 1 === 1 ? 'haut' : 'bas', col: colConfig.columnIndex + 1 }
                      "></label>
                    <div class="finition-grid">
                      @for (finition of colConfig.availableFinitions; track finition.id) {
                        <button
                          class="finition-button"
                          [class.active]="colConfig.finitionId === finition.id"
                          (click)="onColumnFinitionChange(rowConfig.rowIndex, colConfig.columnIndex, finition.id)">
                          @if (finition.imageUrl) {
                            <img [src]="finition.imageUrl" [alt]="finition.name" class="finition-image" />
                          }
                          <span class="finition-name">{{ finition.name }}</span>
                        </button>
                      }
                    </div>
                    @if (colConfig.finitionId) {
                      <button
                        class="apply-all-button"
                        [disabled]="!canApplyColumnFinitionToAll(colConfig.finitionId)"
                        (click)="onApplyColumnFinitionToAll(colConfig.finitionId!)">
                        <span [innerHTML]="'configuration.mobile.bathroom.apply_to_all' | translate"></span>
                      </button>
                    }
                  </div>
                }
              }
            }
          </div>
        }
      </div>

      <!-- Accordéon Positions (niches) -->
      @if (hasNiches()) {
        <div class="accordion-item" [class.expanded]="uiSelection?.activeSection === 'positions'">
          <button class="accordion-header" (click)="setActiveSection('positions')">
            <span class="accordion-title">Positions</span>
            <i
              class="fas"
              [class.fa-chevron-down]="uiSelection?.activeSection !== 'positions'"
              [class.fa-chevron-up]="uiSelection?.activeSection === 'positions'"></i>
          </button>

          @if (uiSelection?.activeSection === 'positions') {
            <div class="accordion-content">
              @for (niche of getNicheInfos(); track niche.rowIndex + '-' + niche.columnIndex) {
                <div class="form-section">
                  <label class="form-label">{{ niche.label }}</label>
                  <div class="button-group">
                    <button
                      class="option-button"
                      [class.active]="niche.isOnLeft"
                      (click)="!niche.isOnLeft && onNichePositionToggle(niche.rowIndex, niche.columnIndex)"
                      [innerHTML]="'configuration.mobile.bathroom.left' | translate"></button>
                    <button
                      class="option-button"
                      [class.active]="!niche.isOnLeft"
                      (click)="niche.isOnLeft && onNichePositionToggle(niche.rowIndex, niche.columnIndex)"
                      [innerHTML]="'configuration.mobile.bathroom.right' | translate"></button>
                  </div>
                </div>
              }
            </div>
          }
        </div>
      }

      <!-- Accordéon Poignées -->
      <div class="accordion-item" id="handles-accordion-item" [class.expanded]="uiSelection?.activeSection === 'handles'">
        <button class="accordion-header" (click)="setActiveSection('handles')">
          <span class="accordion-title" [innerHTML]="'configuration.mobile.bathroom.handles_title' | translate"></span>
          <i
            class="fas"
            [class.fa-chevron-down]="uiSelection?.activeSection !== 'handles'"
            [class.fa-chevron-up]="uiSelection?.activeSection === 'handles'"></i>
        </button>

        @if (uiSelection?.activeSection === 'handles') {
          <div class="accordion-content">
            <!-- Type de poignée (masqué si un seul type disponible) -->
            @if (configuration.handleConfiguration.availableHandleTypes.length > 1) {
              <div class="form-section">
                <label
                  class="form-label"
                  [innerHTML]="'configuration.mobile.bathroom.handle_type_label' | translate"></label>
                <div class="handle-type-grid">
                  @for (option of configuration.handleConfiguration.availableHandleTypes; track option.value) {
                    <button
                      class="handle-type-card"
                      [class.active]="configuration.handleConfiguration.handleType === option.value"
                      [class.disabled]="!option.available"
                      [disabled]="!option.available"
                      (click)="option.available && onHandleTypeChange(option.value)">
                      @if (option.imageUrl) {
                        <img [src]="option.imageUrl" [alt]="option.label" class="handle-type-image" />
                      }
                      <span class="handle-type-label">{{ option.label }}</span>
                    </button>
                  }
                </div>
              </div>
            }

            @if (configuration.handleConfiguration.handleType === HandleType.ToFix) {
              <!-- Longueur -->
              <div class="form-section">
                <label
                  class="form-label"
                  [innerHTML]="'configuration.mobile.bathroom.length_label' | translate"></label>
                <div class="button-group">
                  @for (option of configuration.handleConfiguration.availableLengths; track option.value) {
                    <button
                      class="option-button"
                      [class.active]="configuration.handleConfiguration.handleLength === option.value"
                      [class.disabled]="!option.available"
                      [disabled]="!option.available"
                      (click)="option.available && onHandleLengthChange(option.value)">
                      {{ option.label }}
                    </button>
                  }
                </div>
              </div>

              <!-- Finition des poignées -->
              @if (configuration.handleConfiguration.availableFinitions.length > 0) {
                <div class="form-section" id="handle-finition-section">
                  <label
                    class="form-label"
                    [innerHTML]="'configuration.mobile.bathroom.handle_finish_label' | translate"></label>
                  <div class="finition-grid">
                    @for (finition of configuration.handleConfiguration.availableFinitions; track finition.id) {
                      <button
                        class="finition-button"
                        [class.active]="configuration.handleConfiguration.handleFinitionId === finition.id"
                        (click)="onHandleFinitionChange(finition.id)">
                        @if (finition.imageUrl) {
                          <img [src]="finition.imageUrl" [alt]="finition.name" class="finition-image" />
                        }
                        <span class="finition-name">{{ finition.name }}</span>
                      </button>
                    }
                  </div>
                </div>
              }
            }
          </div>
        }
      </div>

      <!-- Bouton de validation -->
      <div class="validation-button-container">
        <ng-casto-button
          class="validation-button"
          [disabled]="!configuration.isValid"
          (click)="openSummary()"
          [innerHTML]="
            layoutMode === 'desktop'
              ? 'Étape suivante'
              : ('configuration.mobile.bathroom.validate_configuration' | translate)
          "></ng-casto-button>
      </div>
    </div>
  </div>
}

@if (isLoading) {
  <div class="loading-container">
    <div class="spinner"></div>
    <p [innerHTML]="'configuration.mobile.bathroom.loading_configuration' | translate"></p>
  </div>
}

@if (isValidating) {
  <div class="validating-overlay">
    <div class="spinner"></div>
  </div>
}
